import { makeAutoObservable } from 'mobx';

import type { AssistantTurn } from '../components/solution-assistant/solution-assistant-model';
import { agentSettingsStore } from './agent-settings-store';

export type AssistantTab = 'ask' | 'agents';

/** An Agents-panel tab to open on (see `openAgents`). */
export type AgentsPanelTab =
  | 'configuration'
  | 'supervisor-agent'
  | 'knowledge-agent'
  | 'tasks';

/**
 * The header's AI dock (`bobcat-solution-assistant`): whether it is open,
 * which tab ("Ask AI" or "Agents"), and one transcript per solution version,
 * kept while the designer moves between stages. The header toggles it; the
 * Workflows page renders it.
 */
export class SolutionAssistantStore {
  open = false;

  tab: AssistantTab = 'ask';

  /** The Agents panel tab to open on next; consumed when it renders. */
  agentsPanelTab: AgentsPanelTab | null = null;

  /** A node cleared from the context stays cleared until another is selected. */
  clearedRowId: string | null = null;

  private transcripts = new Map<string, AssistantTurn[]>();

  constructor() {
    makeAutoObservable(this);
  }

  toggle() {
    this.open = !this.open;
  }

  openDock() {
    this.openTab('ask');
    this.clearedRowId = null;
  }

  openTab(tab: AssistantTab) {
    this.tab = tab;
    this.open = true;
    this.agentsPanelTab = null;
  }

  /** Opens the dock on Agents, at `panelTab` (e.g. the Knowledge Agent). */
  openAgents(panelTab?: AgentsPanelTab) {
    this.openTab('agents');
    this.agentsPanelTab = panelTab ?? null;
    agentSettingsStore.setTab(
      panelTab === 'knowledge-agent' ? 'knowledge' : 'supervisor'
    );
  }

  setTab(tab: AssistantTab) {
    this.tab = tab;
  }

  close() {
    this.open = false;
  }

  clearNode(rowId: string) {
    this.clearedRowId = rowId;
  }

  turnsFor(solutionKey: string): AssistantTurn[] {
    return this.transcripts.get(solutionKey) ?? [];
  }

  setTurns(solutionKey: string, turns: AssistantTurn[]) {
    this.transcripts.set(solutionKey, turns);
  }
}

export const solutionAssistantStore = new SolutionAssistantStore();
